import { useState, useEffect } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../../foundation/altzero_base/base/components/ui/sheet';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import { billEmailService } from '../../services/billEmailService';

type Props = {
  open: boolean;
  onClose: () => void;
};

function TagList({
  label, hint, items, onChange,
}: {
  label: string;
  hint: string;
  items: string[];
  onChange: (items: string[]) => void;
}) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim().toLowerCase();
    if (v && !items.includes(v)) onChange([...items, v]);
    setDraft('');
  };

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <p className="text-xs text-gray-500">{hint}</p>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Type and press Enter"
        />
        <Button type="button" variant="outline" size="icon" onClick={add}><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-1.5 pt-1">
        {items.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700">
            {v}
            <button onClick={() => onChange(items.filter((i) => i !== v))} className="text-gray-400 hover:text-red-600">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-gray-400">None — no filter applied on this field</span>}
      </div>
    </div>
  );
}

export function InboxConfigDrawer({ open, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [domains, setDomains] = useState<string[]>([]);
  const [pollHours, setPollHours] = useState(6);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    billEmailService.getConfig().then(({ success, data, error: err }) => {
      if (success && data) {
        setDomains(data.sender_domains ?? []);
        setPollHours(data.poll_interval_hours ?? 6);
        setError(null);
      } else {
        setError(err ?? 'Failed to load config');
      }
      setLoading(false);
    });
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    const { success, error: err } = await billEmailService.updateConfig({
      sender_domains: domains,
      poll_interval_hours: pollHours,
    });
    setSaving(false);
    if (success) onClose();
    else setError(err ?? 'Failed to save');
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="inbox-config" side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Invoice inbox settings</SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
        ) : (
          <div className="mt-4 space-y-5">
            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              Every email with a PDF or Word attachment is pulled in — no subject or sender filtering happens
              at ingestion, so a retailer you haven't listed below still gets picked up. Unwanted emails are a
              one-click Ignore from the inbox.
            </p>

            <TagList
              label="Trusted senders"
              hint={'Emails from these domains are tagged "Trusted" in the inbox for quicker triage — this does not filter anything out.'}
              items={domains}
              onChange={setDomains}
            />

            <div className="space-y-1.5">
              <Label>Poll every (hours)</Label>
              <Input
                type="number"
                data-testid="inbox-config-poll-hours"
                min={1}
                max={24}
                value={pollHours}
                onChange={(e) => setPollHours(Math.max(1, Math.min(24, Number(e.target.value) || 1)))}
                className="w-24"
              />
            </div>

            {error && <p data-testid="inbox-config-error" className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button data-testid="inbox-config-save" disabled={saving} onClick={handleSave}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save settings'}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
